// src/components/AlternateSection/AlternateSection.tsx
'use client'

import React from 'react'
import { PortableTextObject } from 'sanity'
import { usePathname } from 'next/navigation'

import { styles, ASectionVariants } from './AlternateSection.css'
import { SanityImageAssetDocument } from 'next-sanity'
import { SanityButtonProps } from '@otomoro/components/shared/Button'
import { SanityImage } from '@otomoro/components/shared/SanityImage'
import { AlternateContent } from '@otomoro/components/shared/AlternateContent/AlternateContent'
import { DownloadEmail } from '@otomoro/components/shared/DownloadEmail/DownloadEmail'
import { Tag } from '@otomoro/components/shared/Tag'

interface AlternateSectionProps extends ASectionVariants {
  _key: string
  tag: string
  headline: PortableTextObject
  description?: PortableTextObject
  descriptionType: 'single' | 'multiple'
  descriptionPairs: any[]
  button: SanityButtonProps
  image: SanityImageAssetDocument
  theme: 'dark' | 'light'
  alternateSide: boolean
  showDL: boolean
  downloadInfo: {
    title: string
    fileUrl: string
    fileName: string
    tag: string
  }
  showLogo: boolean
  cardLogo: any
  page?: 'home' | 'default' | 'technologyPage'
}

export const AlternateSection: React.FC<AlternateSectionProps> = (props) => {
  const {
    _key,
    tag,
    headline,
    description,
    button,
    image,
    theme,
    alternateSide,
    descriptionType,
    descriptionPairs,
    showDL,
    downloadInfo,
    showLogo,
    cardLogo,
    page: pageProp,
  } = props

  const pathname = usePathname()
const pageMap = {
  '/': 'home',
  '/technology': 'technologyPage',
} as const;

const page =
  pageProp ?? pageMap[pathname as keyof typeof pageMap] ?? 'default';

  const { wrapper, base, illustrationSide, informationsSide, cardLogoStyle, imageStyle } = styles({
    theme,
    page,
    alternateSide,
    pairs: descriptionType !== 'single',
  })

  return (
    <div className={wrapper()}>
      <section className={base()}>
        {/* left column */}
        <div className={informationsSide()}>
          <Tag theme={theme} className="">
            {tag}
          </Tag>
          <AlternateContent
            _key={_key}
            headline={headline}
            theme={theme}
            button={button}
            descriptionPairs={descriptionPairs}
            description={description}
            descriptionType={descriptionType}
            showDL={showDL}
            downloadInfo={downloadInfo}
            showLogo={showLogo}
            pairsImage={image}
            headingLevel={pathname === '/impact' || pathname === '/' ? 'h2' : 'h1'}
          />
        </div>

        {/* right column */}
        <div className={illustrationSide()}>
          {/* stack invisible tag + logo + image */}
          <div className="flex flex-col gap-y-2 h-full w-full">
            <Tag theme={theme} className="invisible">
              {tag}
            </Tag>

            {cardLogo?.svg && (
              <div className={cardLogoStyle()}>
                {/* eslint-disable-next-line react/no-danger */}
                <span dangerouslySetInnerHTML={{ __html: cardLogo.svg }} />
              </div>
            )}

            <div className="relative w-full h-full">
              <SanityImage
                className={imageStyle()}
                image={image.image}
                alt={image.alt || ''}
                sizes="(max-width: 768px) 80vw,
                       (max-width: 1200px) 50vw,
                       60vw"
              />
            </div>
          </div>
        </div>

        {showDL && (
          <span className="block lg:hidden">
            <DownloadEmail _key={_key} infos={downloadInfo} theme={theme} center noPx altMt />
          </span>
        )}
      </section>
    </div>
  )
}

export default AlternateSection

  